/**
 * PATH: packages/email-hub/src/feature/components/rich-text/greetingSuggest.ts
 * FILE_ID: EMAILHUB-RICHTEXT-GREETING-001
 * PURPOSE: Suggest a person's name while typing a greeting such as "Hi x".
 * ROLE IN ARCHITECTURE: TipTap plugin beside @mention. Inserts plain text, not a chip.
 * MAIN EXPORTS: greetingNamePrefix, orderGreetingMatches, greetingInsertion, buildGreetingSuggest
 * NON-RESPONSIBILITIES: Does not search the network itself. The editor passes searchContacts.
 * NOTES FOR FUTURE AI: Keep the trigger to a real greeting word. Do not steal keys from @ or /.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'

import type { MentionPerson } from './mention'
import type { SuggestionController } from './suggestionController'

const GREETING = String.raw`(?:hi|hey|hello|dear|good\s+(?:morning|afternoon|evening))`
const GREETING_PREFIX = new RegExp(
  String.raw`(?:^|[.!?]\s+)${GREETING}\b[,!]?\s+(\S+)$`,
  'i',
)

/** The letters typed after a greeting, or null when the caret is not in one. */
export function greetingNamePrefix(textBeforeCaret: string): string | null {
  const match = textBeforeCaret.match(GREETING_PREFIX)
  const prefix = match?.[1] ?? ''
  if (!prefix || prefix.includes('@')) return null
  return prefix
}

export function greetingInsertion(name: string, email: string): string {
  const local = email.split('@')[0] ?? ''
  const label = (name || local).trim()
  if (!label) return ''
  return label.endsWith(',') ? `${label} ` : `${label}, `
}

/** First-name matches first, then later words. Drops people who do not start with the letters. */
export function orderGreetingMatches(people: readonly MentionPerson[], prefix: string): MentionPerson[] {
  const needle = prefix.toLowerCase()
  const first: MentionPerson[] = []
  const later: MentionPerson[] = []
  for (const person of people) {
    const parts = person.name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) continue
    if (parts[0].toLowerCase().startsWith(needle)) first.push(person)
    else if (parts.slice(1).some((part) => part.toLowerCase().startsWith(needle))) later.push(person)
  }
  return [...first, ...later].slice(0, 6)
}

function textBeforeCaret(view: EditorView): string {
  const from = view.state.selection.from
  const blockStart = view.state.selection.$from.start()
  return view.state.doc.textBetween(blockStart, from, '\n', '\n')
}

function caretRect(view: EditorView): { top: number; left: number; bottom: number } {
  try {
    const coords = view.coordsAtPos(view.state.selection.from)
    return { top: coords.top, left: coords.left, bottom: coords.bottom }
  } catch {
    return { top: 0, left: 0, bottom: 0 }
  }
}

export function buildGreetingSuggest(
  controller: SuggestionController,
  search: (query: string, signal: AbortSignal) => Promise<MentionPerson[]>,
) {
  return Extension.create({
    name: 'greetingSuggest',
    addProseMirrorPlugins() {
      const key = new PluginKey('greetingSuggest')
      return [
        new Plugin({
          key,
          props: {
            handleKeyDown(view, event) {
              const state = controller.get()
              if (!state || state.kind !== 'greeting') return false
              if (event.key === 'Escape') {
                controller.set(null)
                controller.setActive(null)
                return true
              }
              if (state.items.length === 0) return false
              if (event.key === 'ArrowDown') {
                controller.setIndex((state.index + 1) % state.items.length)
                return true
              }
              if (event.key === 'ArrowUp') {
                controller.setIndex((state.index - 1 + state.items.length) % state.items.length)
                return true
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault()
                controller.select(state.index)
                return true
              }
              return false
            },
          },
          view(editorView) {
            let timer: ReturnType<typeof setTimeout> | null = null
            let inflight: AbortController | null = null
            let lastPrefix = ''
            const close = () => {
              if (timer) clearTimeout(timer)
              timer = null
              inflight?.abort()
              inflight = null
              lastPrefix = ''
              if (controller.get()?.kind === 'greeting') {
                controller.set(null)
                controller.setActive(null)
              }
            }
            const publish = (view: EditorView, prefix: string) => {
              const ctrl = new AbortController()
              inflight = ctrl
              const opened = controller.get()
              if (!opened || opened.kind === 'greeting') {
                controller.set({
                  kind: 'greeting',
                  items: opened?.kind === 'greeting' ? opened.items : [],
                  index: 0,
                  rect: caretRect(view),
                  loading: true,
                })
              }
              void search(prefix, ctrl.signal)
                .then((people) => {
                  if (ctrl.signal.aborted) return
                  if (greetingNamePrefix(textBeforeCaret(view)) !== prefix) return
                  const owner = controller.get()
                  if (owner && owner.kind !== 'greeting') return
                  const matches = orderGreetingMatches(people, prefix)
                  controller.setActive({
                    raw: matches,
                    command: (item) => {
                      const person = item as MentionPerson
                      const typed = greetingNamePrefix(textBeforeCaret(view))
                      const insert = greetingInsertion(person.name, person.email)
                      if (!typed || !insert || orderGreetingMatches([person], typed).length === 0) {
                        close()
                        return
                      }
                      const to = view.state.selection.from
                      const from = to - typed.length
                      view.dispatch(view.state.tr.insertText(insert, from, to))
                      close()
                    },
                  })
                  controller.set({
                    kind: 'greeting',
                    items: matches.map((person) => ({
                      id: person.email || person.name,
                      title: person.name || person.email,
                      hint: person.email,
                    })),
                    index: 0,
                    rect: caretRect(view),
                    loading: false,
                  })
                })
                .catch(() => {
                  if (!ctrl.signal.aborted) close()
                })
            }
            const schedule = (view: EditorView) => {
              const owner = controller.get()
              if (owner && owner.kind !== 'greeting') return
              const prefix = greetingNamePrefix(textBeforeCaret(view))
              if (!prefix) {
                close()
                return
              }
              if (prefix === lastPrefix && owner?.kind === 'greeting') {
                controller.set({ ...owner, rect: caretRect(view) })
                return
              }
              lastPrefix = prefix
              if (timer) clearTimeout(timer)
              inflight?.abort()
              timer = setTimeout(() => publish(view, prefix), 180)
            }
            return {
              update(view, prev) {
                if (view.state.doc.eq(prev.doc) && view.state.selection.eq(prev.selection)) return
                schedule(view)
              },
              destroy: close,
            }
          },
        }),
      ]
    },
  })
}
